import type { ReadingSentence } from "@/lib/reading";
import type { ParsedToken } from "@/lib/search";

/**
 * What a log is made of, and the pure rules over it.
 *
 * **This is a scrollback, not a search box** — the design handoff calls treating it as
 * "input → results" the single most likely thing to get wrong. Captures accumulate newest-last, the
 * unfocused ones dim, any of them can be scrolled back to and edited in place, and each one owns two
 * image slots that every card mined from it inherits.
 *
 * **The hook that used to live here is `useReading` now.** 日本-207 turned one in-memory log into
 * many persisted ones, so ownership moved to a hook that also holds the folders, the open tabs and
 * the library. What stayed is this file's vocabulary — the design was explicit that `useReadingLog`
 * already existed and would not have to be renamed, and `LogEntry` is still the word for a row on
 * screen. The pure functions live here for their own sake: `npm test` has no DOM, so a rule is only
 * reachable if it is not inside a hook.
 */

export interface LogEntry {
  /**
   * A UUID minted by the CLIENT, not the server.
   *
   * Forced rather than tidy. `append` has to hand an id to the tray agent's screenshot upload
   * *synchronously*, before the parse request has even been sent — see `Reading.append` — and a
   * server-assigned id would have to be reconciled back through every piece of Read's state that
   * keys on one: `pendingShots`, `selected`, `editing`, `expanded`.
   *
   * It was a number until 日本-207, when it also had to survive a reload.
   */
  id: string;
  /** Wall-clock capture time; the log's 42px gutter shows HH:MM. Also the log's sort key. */
  at: Date;
  text: string;
  tokens: ParsedToken[];
  /** Anki field values, e.g. `<img src="capture:…">`. What a mined card inherits verbatim. */
  image: string;
  extraImage: string;
  /**
   * The notes mined from this sentence. Real ids since 日本-207, where it was a bare count.
   *
   * The count is what a row and the library's delete confirmation show; the ids are what make
   * "61 mined cards" a fact rather than a tally that drifts.
   */
  minedNoteIds: number[];
  /**
   * A slot waiting on the tray agent's screenshot upload, or null.
   *
   * Per entry rather than in `Read`'s component state, and that is forced rather than tidy: a
   * capture can arrive while you are on Cards, and a rail click unmounts `Read` — which is why the
   * log lives in `App` at all. State held inside `Read` is simply unreachable from the code that
   * receives a capture.
   *
   * **Local only, never persisted.** It describes an upload happening in another process right
   * now; a reload that restored it would show `storing…` for ever.
   */
  imageBusy: "image" | "extraImage" | null;
  /**
   * The English, when the reader asked for it (日本-102). Empty means bare.
   *
   * **A field, not a message.** It persists with the sentence, survives a tab close and a reload,
   * and is editable — the reader's correction beats the model's. It is never copied onto a mined
   * card.
   *
   * There is deliberately **no stale flag beside it**: editing the Japanese does not mark, strike
   * through or invalidate the translation. Explicitly rejected in the design as ceremony, and it
   * is `LogEntry.imageBusy`'s rule from the other direction — one fact, one spelling.
   */
  translation: string;
  /**
   * A translate request in flight for this row, drawn as `translating…`.
   *
   * **Local only, never persisted**, exactly like `imageBusy`: it describes a request happening
   * right now, and a reload that restored it would show `translating…` for ever.
   */
  translating: boolean;
}

/** The wire row as the screen wants it. One place, so the two shapes cannot drift. */
export function toEntry(sentence: ReadingSentence, tokens: ParsedToken[] = []): LogEntry {
  return {
    id: sentence.id,
    // Seconds on the wire, milliseconds in `Date` — the one unit conversion in the round trip, and
    // it lives here so no caller has to remember it.
    at: new Date(sentence.capturedAt * 1000),
    text: sentence.text,
    tokens,
    image: sentence.image,
    extraImage: sentence.extraImage,
    minedNoteIds: sentence.minedNoteIds,
    imageBusy: null,
    translation: sentence.translation ?? "",
    translating: false,
  };
}

/**
 * Which capture has focus after `removedId` goes.
 *
 * The one above it, falling back to the one below when you delete the oldest. Not "the newest" —
 * deleting a bad line while scrolled back through an hour of reading should leave you where you
 * were looking, not throw you to the bottom of the log.
 *
 * Named `nextFocusId` since 日本-306, where `activeId` became `focusedId`. The rule is unchanged;
 * what changed is that focus is now something you can also set by clicking a row, so the word
 * "active" would have had to mean two things at once.
 *
 * Exported for its own sake: `npm test` has no DOM, so a hook's behaviour is only reachable through
 * a pure function like this one.
 */
export function nextFocusId(
  before: readonly LogEntry[],
  after: readonly LogEntry[],
  focusedId: string | null,
  removedId: string,
): string | null {
  if (focusedId !== removedId) return focusedId;
  const index = before.findIndex((e) => e.id === removedId);
  if (index === -1) return focusedId;
  return (after[index - 1] ?? after[index])?.id ?? null;
}

/**
 * Where a sentence belongs in a log, by timestamp.
 *
 * **A moved sentence lands in timestamp order, never at the end** — a log is a record of reading,
 * so it must not claim a sentence was read last. The server orders by `(capturedAt, id)`; this is
 * the same rule applied locally so the row appears in the right place immediately rather than
 * jumping when the response lands.
 */
export function insertByTime(entries: readonly LogEntry[], entry: LogEntry): LogEntry[] {
  const next = entries.filter((e) => e.id !== entry.id);
  const at = next.findIndex(
    (e) => e.at.getTime() > entry.at.getTime() || (e.at.getTime() === entry.at.getTime() && e.id > entry.id),
  );
  if (at === -1) return [...next, entry];
  return [...next.slice(0, at), entry, ...next.slice(at)];
}

/**
 * Reconcile a freshly fetched log with rows this device already had.
 *
 * **Merge, never replace**, and the case that forces it is the desktop shell's cold start: the tray
 * agent launches the app *with* a capture in the URL, so `append` writes the new sentence locally
 * while the fetch for that log is still in flight. A plain replace would drop the very sentence the
 * window was opened to show — the server's response was built before its POST landed — and it would
 * reappear only on the next load.
 *
 * The fetched copy wins for anything present in both — its text, images and translation are what
 * the server holds. **Except the tokens**, which it no longer carries: since 日本-602 a log arrives
 * unparsed, so a fetched copy's `tokens` are always empty. A capture already parsed locally keeps
 * its own, provided the text is identical; different text means the sentence was edited elsewhere
 * and the local tokens describe the old one. Taking the empty copy instead was 日本-714 — the
 * id was already recorded as parsed, so nothing ever asked again.
 *
 * Local-only rows are placed by timestamp rather than appended, for `insertByTime`'s reason — a
 * log is a record of reading and must not claim a sentence was read last.
 */
export function mergeLoaded(
  fetched: readonly LogEntry[],
  local: readonly LogEntry[],
): LogEntry[] {
  const seen = new Set(fetched.map((entry) => entry.id));
  const localById = new Map(local.map((entry) => [entry.id, entry]));
  let merged = fetched.map((entry) => {
    const mine = localById.get(entry.id);
    return entry.tokens.length === 0 && mine && mine.tokens.length > 0 && mine.text === entry.text
      ? { ...entry, tokens: mine.tokens }
      : entry;
  });
  for (const row of local) {
    if (!seen.has(row.id)) merged = insertByTime(merged, row);
  }
  return merged;
}

/** Every distinct term in the log, which is what the sentence underlines are drawn from. */
export function termsInLog(entries: readonly LogEntry[]): string[] {
  const terms = new Set<string>();
  for (const entry of entries) {
    for (const token of entry.tokens) {
      if (token.dictionaryForm) terms.add(token.dictionaryForm);
      terms.add(token.surface);
    }
  }
  return [...terms];
}

/**
 * Every distinct term **on screen** — the log's tokens *and* the dock's results.
 *
 * The results are the half that is easy to forget, and forgetting it is a lie rather than a gap:
 * hovering よう returns 形, 酔う, 用, 益 … none of which appear in the sentence, so none of which
 * were ever asked about, so every one of them rendered `not in collection` — including the ones you
 * carded years ago. A result's badge and its `+ add` / `✓ in deck` button both hang off this.
 */
export function termsOnScreen(
  entries: readonly LogEntry[],
  results: readonly { term: string | null }[],
): string[] {
  const terms = new Set(termsInLog(entries));
  for (const result of results) {
    if (result.term) terms.add(result.term);
  }
  return [...terms];
}
